import { makeAutoObservable, reaction } from "mobx";
import _ from "lodash";
import { RootStore, LangStore } from "../../../store";
import { StoreStatus, CallbackDataParams } from "../../../store/types";

import { getApi, getApiError, isSessionExpired } from "../../../api";
import { tokenManager } from "../../../api/tokenManager";
import { CreateEntityStore } from "../../common/store/types";
import { newIdempotencyKey, requestFingerprint } from "../../../common/utils";
import { musicalWorkService } from "../services/musicalWorkService";
import {
  MusicalWorkValuesInit,
  getMusicalWorkCreateVariables,
  getMusicalWorkValues,
  MusicalWorkValuesType,
  MusicalWorkErrorsType,
  musicalWorkDisplayId,
} from "./MusicalWorkCommonStore";
import { MusicalWork, SortOrder } from "../../../api/graphql";
import {
  CustomValidator,
  runCustomValidators,
  collectCustomWarnings,
} from "../../common/validation/types";
import * as customValidations from "./customValidations";
import { resolveCurrentLang } from "../../../common/utils";
import { processTerritoriesSelection, optimizeTerritoriesForSave, validateTerritoriesExclusivity } from "../../territory/utils/territorySelectionHelper";
import {
  getMusicalGenreValues,
  MusicalGenreValuesType,
} from "../../musicalGenre/store/MusicalGenreCommonStore";
import { GridColumn } from "../../common/types";
import { WorkAuthorCreateStore } from "../../workAuthor/store";
import {
  getInterpreterValues,
  InterpreterValuesType,
} from "../../interpreter/store/InterpreterCommonStore";
import {
  getTerritoryValues,
  TerritoryValuesType,
} from "../../territory/store/TerritoryCommonStore";
import { addedStatusList } from "../../common/types";
import { formatBytes } from "../../../common/utils";
import { PhonogramCreateStore } from "../../phonogram/store";
import { apiUploadFile } from "../../../api/apiClient";
import { uploadFailureReason } from "../../../common/utils";
import { GridChangeSet } from "../../common/types";
import { confirm } from "../../../common/services/confirmService";
import { getApiAsync } from "../../../api";
import { restoredStatus } from "../../common/types";
import { updatedStatus, deleteStatus } from "../../common/types";
import { isOriginalCrudStatus } from "../../common/types";
import { musicalGenreService } from "../../musicalGenre/services/musicalGenreService";
import { interpreterService } from "../../interpreter/services/interpreterService";
import { territoryService } from "../../territory/services/territoryService";
import { InitializeParams } from "../../../store/types";

type AsyncFunction = () => Promise<any>;

export class MusicalWorkCreateStore implements CreateEntityStore {
  readonly rootStore: RootStore;
  readonly langStore: LangStore;

  status: StoreStatus = "uninitialized";
  storeId?: string;
  originalError: any;
  errorDialogOpened: boolean = false;
  validationErrorsDialogOpen: boolean = false;
  errorTitle: string = "Error";
  errorDescription: string = "Hubo un error";
  errors: MusicalWorkErrorsType = {};
  service = musicalWorkService;
  cardState: Record<string, { open: boolean; errors: string[] }> = {
    null: { open: true, errors: [] },
    workInfo: { open: true, errors: [] },
    workRegistration: { open: true, errors: [] },
    workContent: { open: true, errors: [] },
    contracts: { open: true, errors: [] },
    systemDates: { open: false, errors: [] },
  };
  navigationLinks: { id: string; label: string; errors: string }[] = [];
  savedId: string | null = null;
  // Mientras guarda: la capa del formulario (SavingOverlay) lo bloquea.
  isSaving: boolean = false;
  savedEntity: MusicalWork | null = null;
  cancelled = false;
  callbackParams: CallbackDataParams | null = null;
  entityValues = MusicalWorkValuesInit();
  titleError: string | undefined;
  musicalGenreError: string | undefined;
  musicalGenreService = musicalGenreService;
  musicalGenreSearchValue: string = "";
  musicalGenreOptions: MusicalGenreValuesType[] = [];
  alternativeTitlesError: string | undefined;
  authorsError: string | undefined;
  authorsVisibleColumns: GridColumn[];
  authorsCreateStore: WorkAuthorCreateStore;
  notesError: string | undefined;
  performersError: string | undefined;
  performersService = interpreterService;
  performersSearchValue: string = "";
  performersOptions: InterpreterValuesType[] = [];
  cmsIdError: string | undefined;
  territoriesError: string | undefined;
  territoriesService = territoryService;
  territoriesSearchValue: string = "";
  territoriesOptions: TerritoryValuesType[] = [];
  iswcError: string | undefined;
  scoreError: string | undefined;
  phonogramsError: string | undefined;
  phonogramsVisibleColumns: GridColumn[];
  phonogramsCreateStore: PhonogramCreateStore;
  originalLyricsError: string | undefined;
  contractObservationError: string | undefined;
  createdAt_Error: string | undefined;
  lastUpdatedAt_Error: string | undefined;
  private uniqueCounter = 1;
  dummyCRUDRestoredObservable: number = 0;
  private customValidators: CustomValidator<MusicalWorkValuesType>[] = [];
  private _ownPendingChanges = false;
  // La clave de idempotencia del guardado en curso y lo que mandó: un reintento
  // que manda lo mismo (sin cambios desde el intento que falló) la reusa, y el
  // server lo reconoce si ya lo había aplicado; cualquier cambio, o un guardado
  // exitoso, la renueva.
  private saveAttempt: { key: string; request: string } | null = null;

  private idempotencyKeyFor(variables: object): string {
    const request = requestFingerprint(variables);
    if (!this.saveAttempt || this.saveAttempt.request !== request) {
      this.saveAttempt = { key: newIdempotencyKey(), request };
    }
    return this.saveAttempt.key;
  }

  private originalSnapshot: Record<string, any> | null = null;
  private stopReaction: (() => void) | null = null;
  // Callback system for "Add new" flow
  isWaitingForCallback = false;
  callbackReturnPath: string | null = null;
  // Modal mode flag - when true, reference views open as stacked modals
  isModalMode = false;
  // Handler for inline create dialog — when set, close() calls this instead of navigating
  closeHandler:
    | ((returnData?: {
        resultId?: string;
        cancelled?: boolean;
        entity?: any;
      }) => void)
    | null = null;
  constructor(rootStore: RootStore, storeId?: string) {
    this.rootStore = rootStore;
    this.langStore = rootStore.langStore;
    this.storeId = storeId;

    this.setNavigationLinks();
    this.authorsVisibleColumns = [
      { name: "__author", title: this.langStore.WorkAuthorAuthorLabel },
      {
        name: "lyricsPercentage",
        title: this.langStore.WorkAuthorLyricsPercentageLabel,
      },
      {
        name: "musicPercentage",
        title: this.langStore.WorkAuthorMusicPercentageLabel,
      },
    ];
    this.authorsCreateStore = new WorkAuthorCreateStore(
      rootStore,
      "authors_" + this.storeId
    );
    this.phonogramsVisibleColumns = [
      { name: "audioFile", title: this.langStore.PhonogramAudioFileLabel },
      { name: "trackName", title: this.langStore.PhonogramTrackNameLabel },
      { name: "isrc", title: this.langStore.PhonogramIsrcLabel },
      { name: "albumName", title: this.langStore.PhonogramAlbumNameLabel },
      {
        name: "publicationYear",
        title: this.langStore.PhonogramPublicationYearLabel,
      },
    ];
    this.phonogramsCreateStore = new PhonogramCreateStore(
      rootStore,
      "phonograms_" + this.storeId
    );
    // Auto-registra los validators custom exportados por customValidations.ts
    // (stub editable del proyecto). Evita tener que editar este store generado.
    Object.values(customValidations).forEach((v) => {
      if (v && typeof (v as any).validate === "function") {
        this.registerCustomValidator(
          v as CustomValidator<MusicalWorkValuesType>
        );
      }
    });
    makeAutoObservable(this);
    this.setupChangeDetection();
    this.saveOriginalState();
  }

  destroy() {
    if (this.stopReaction) {
      this.stopReaction();
    }
  }

  setCallbackParams(callbackParams: CallbackDataParams | null) {
    if (callbackParams?.callerData) {
      this.callbackParams = callbackParams;
    }
  }

  registerCustomValidator(validator: CustomValidator<MusicalWorkValuesType>) {
    this.customValidators.push(validator);
  }

  // Acceso resiliente a cardState: un card referenciado por un form pero no
  // inicializado (p.ej. un card manual del proyecto) NO debe crashear la página.
  // Los getters son puros (no mutan durante el render); los mutadores auto-crean
  // la entrada faltante con un default abierto y sin errores.
  toggleCardState(id: string) {
    if (!this.cardState[id]) this.cardState[id] = { open: true, errors: [] };
    this.cardState[id].open = !this.cardState[id].open;
  }

  setCardState(id: string, status: boolean) {
    if (!this.cardState[id]) this.cardState[id] = { open: true, errors: [] };
    this.cardState[id].open = status;
  }

  getCardState(id: string) {
    return this.cardState[id]?.open ?? true;
  }

  getCardErrors(id: string) {
    return this.cardState[id]?.errors?.join(", ") ?? "";
  }

  setCardError(id: string, message: string) {
    const state = this.cardState[id] ?? { open: true, errors: [] };
    this.cardState = {
      ...this.cardState,
      ...{
        [id]: { open: state.open, errors: [...state.errors, message] },
      },
    };
  }

  setStatus(status: StoreStatus) {
    this.status = status;
  }

  get processingTitle() {
    return this.langStore.processingTitle;
  }

  get processingDescription() {
    return this.langStore.processingDescription;
  }

  get isProcessing() {
    return this.status === "processing";
  }

  get displayId(): string {
    if (this.entityValues) {
      return musicalWorkDisplayId(this.entityValues as any, this.langStore);
    }
    return "";
  }

  get breadcrumbLabel(): string {
    const displayId = this.displayId;
    if (displayId) {
      return displayId;
    }
    return this.langStore.MusicalWorkSingular;
  }

  closeErrorDialog() {
    this.errorDialogOpened = false;
  }

  showValidationErrorsDialog() {
    this.validationErrorsDialogOpen = true;
  }

  closeValidationErrorsDialog() {
    this.validationErrorsDialogOpen = false;
  }

  setContentTop(_contentTop: number) {}

  setCRUDRowsFromValues(changes: GridChangeSet, values: { string: any }) {
    if (changes.added) {
      changes.added = [values];
    } else if (changes.changed) {
      changes.changed = [values];
    }
  }

  *returnedData(params: CallbackDataParams): any {
    if (
      params.returnData &&
      !params.returnData.cancelled &&
      (params.returnData.entity || params.returnData.resultId)
    ) {
      // Delegate to subentity CreateStore if callerData.entityName matches a subentity
      if (
        ["WorkAuthor", "WorkAuthorPublisher"].includes(
          params.callerData?.entityName as string
        )
      ) {
        this.authorsCreateStore.isWaitingForCallback = false;
        this.authorsCreateStore.callbackReturnPath = null;
        yield this.authorsCreateStore.returnedData(params);
        return;
      }
      if (
        ["Phonogram", "MusicalWorkLink"].includes(
          params.callerData?.entityName as string
        )
      ) {
        this.phonogramsCreateStore.isWaitingForCallback = false;
        this.phonogramsCreateStore.callbackReturnPath = null;
        yield this.phonogramsCreateStore.returnedData(params);
        return;
      }

      if (params.callerData?.fieldName === "musicalGenre") {
        if (params.returnData.entity) {
          const displayId =
            params.returnData.entity.displayId ||
            String(params.returnData.entity.id);
          this.entityValues.showMusicalGenre = {
            displayId,
            id: params.returnData.entity.id,
          };
        } else if (params.returnData.resultId) {
          const api = yield* getApi(
            this.rootStore.authStore,
            this.rootStore.activityStore
          );
          const result = yield api.musicalGenreOne({
            id: params.returnData.resultId,
          });
          const musicalGenre = result.musicalGenreOne;
          this.entityValues.showMusicalGenre = {
            displayId: musicalGenre.displayId,
            id: musicalGenre.id,
          };
          this.setMusicalGenreOptions([
            getMusicalGenreValues(musicalGenre, this.langStore),
          ]);
        }
      } else if (params.callerData?.fieldName === "performers") {
        if (params.returnData.entity) {
          const displayId =
            params.returnData.entity.displayId ||
            String(params.returnData.entity.id);
          this.entityValues.showPerformers.push({
            displayId,
            id: params.returnData.entity.id,
          });
        } else if (params.returnData.resultId) {
          const api = yield* getApi(
            this.rootStore.authStore,
            this.rootStore.activityStore
          );
          const result = yield api.interpreterOne({
            id: params.returnData.resultId,
          });
          const interpreter = result.interpreterOne;
          this.entityValues.showPerformers.push({
            displayId: interpreter.displayId,
            id: interpreter.id,
          });
          this.setPerformersOptions([
            getInterpreterValues(interpreter, this.langStore),
          ]);
        }
      } else if (params.callerData?.fieldName === "territories") {
        if (params.returnData.entity) {
          const displayId =
            params.returnData.entity.displayId ||
            String(params.returnData.entity.id);
          this.entityValues.showTerritories.push({
            displayId,
            id: params.returnData.entity.id,
          });
        } else if (params.returnData.resultId) {
          const api = yield* getApi(
            this.rootStore.authStore,
            this.rootStore.activityStore
          );
          const result = yield api.territoryOne({
            id: params.returnData.resultId,
          });
          const territory = result.territoryOne;
          this.entityValues.showTerritories.push({
            displayId: territory.displayId,
            id: territory.id,
          });
          this.setTerritoriesOptions([
            getTerritoryValues(territory, this.langStore),
          ]);
        }
      }
    }
  }

  showErrorDialog(originalError?: any, title?: string, description?: string) {
    // isSessionExpired y no una comparación a mano: los llamadores pasan a
    // veces el Error crudo y a veces el código ya resuelto, y cada store tenía
    // su propia versión del chequeo —o ninguna—, así que la sesión vencida se
    // detectaba o no según por dónde hubiera entrado el error.
    if (isSessionExpired(originalError, this.langStore)) {
      this.rootStore.authStore.openSessionExpiredDialog(() => {});
      return;
    }

    this.originalError = originalError;
    if (title) {
      this.errorTitle = title;
    }
    if (description) {
      this.errorDescription = description;
    }
    this.errorDialogOpened = true;
  }

  setNavigationLinks() {
    this.navigationLinks = [
      {
        id: "workInfo",
        label: this.rootStore.langStore.groupMusicalWorkWorkInfoLabel,
        errors: this.getCardErrors("workInfo"),
      },
      {
        id: "workRegistration",
        label: this.rootStore.langStore.groupMusicalWorkWorkRegistrationLabel,
        errors: this.getCardErrors("workRegistration"),
      },
      {
        id: "workContent",
        label: this.rootStore.langStore.groupMusicalWorkWorkContentLabel,
        errors: this.getCardErrors("workContent"),
      },
      {
        id: "contracts",
        label: this.rootStore.langStore.groupMusicalWorkContractsLabel,
        errors: this.getCardErrors("contracts"),
      },
      {
        id: "systemDates",
        label: this.rootStore.langStore.groupMusicalWorkSystemDatesLabel,
        errors: this.getCardErrors("systemDates"),
      },
    ];
  }

  getTitleValue(): string {
    return this.entityValues.title;
  }

  setTitleValue(value: string) {
    this.entityValues.title = value;
  }

  setMusicalGenreOptions(values: MusicalGenreValuesType[]) {
    this.musicalGenreOptions = values;
  }

  *loadMusicalGenreValues(): any {
    const cache = this.rootStore.referenceCacheStore;
    yield cache.ensureLoaded("MusicalGenre");
    const allOptions = cache.getOptions(
      "MusicalGenre"
    ) as MusicalGenreValuesType[];

    const searchValue = this.musicalGenreSearchValue?.toLowerCase() || "";
    let filtered = searchValue
      ? allOptions.filter((item: any) =>
          item.displayId.toLowerCase().includes(searchValue)
        )
      : [...allOptions];

    filtered.sort((a: any, b: any) => a.displayId.localeCompare(b.displayId));
    this.setMusicalGenreOptions(filtered);
  }

  getMusicalGenreOptions() {
    return this.musicalGenreOptions.map((x: any) => {
      return {
        displayId: resolveCurrentLang(x.displayId, this.langStore),
        id: x.id,
      };
    });
  }

  *setMusicalGenreValue(value: string) {
    this.musicalGenreSearchValue = value;
    yield this.loadMusicalGenreValues();
  }

  // eslint-disable-next-line require-yield -- extension point: users may inject `yield this.helper(...)` after generation; see workAuthor.copyAuthorPublishersToWorkAuthor
  *setSelectedMusicalGenre(value: { displayId: string; id: string } | null) {
    if (value?.id === "add-new") {
      const notFound = /".*?"/.exec(value.displayId);
      this.addNewMusicalGenre(
        notFound ? notFound[0].replaceAll('"', "") : undefined,
        "musicalGenre"
      );
    } else {
      if (value) {
        this.entityValues.showMusicalGenre = value;
        this.entityValues.__musicalGenre = value.displayId;
      }
    }
  }

  getAlternativeTitlesValue(): string[] {
    return this.entityValues.alternativeTitles;
  }

  setAlternativeTitlesValue(value: string[]) {
    this.entityValues.alternativeTitles = value;
  }

  get authorsFooter() {
    let totalLyricsPercentage = 0;
    let totalMusicPercentage = 0;
    for (const workAuthor of this.entityValues.showAuthors) {
      totalLyricsPercentage += Number(workAuthor.lyricsPercentage) || 0;
      totalMusicPercentage += Number(workAuthor.musicPercentage) || 0;
    }

    const formatPercentage = (value: number) => {
      const rounded = Math.round(value * 100) / 100;
      return rounded % 1 === 0 ? `${rounded}%` : `${rounded.toFixed(2)}%`;
    };

    const alert = {sx: { fontSize: 20, color: 'error.main', fontWeight: 'bold' }};
    const normal = {sx: { fontSize: 20, color: 'success.main', fontWeight: 'bold' }};
    return {
      lyricsPercentage: {value: formatPercentage(totalLyricsPercentage), ...(totalLyricsPercentage > 100 ? alert : normal)},
      musicPercentage: {value: formatPercentage(totalMusicPercentage), ...(totalMusicPercentage > 100 ? alert : normal)}
    }
  }

  authorsLoadRow(row: any) {
    this.authorsCreateStore.loadValuesFromRow(row);
  }

  authorsRestore(id: string) {
    const authors = this.entityValues.showAuthors.filter((x) => x.id === id)[0];
    if (authors) {
      authors.__status__ = restoredStatus(authors.__status__);
    }
    this.dummyCRUDRestoredObservable = this.uniqueCounter++;
  }

  authorsValidateChanges(_changes: GridChangeSet) {
    const errors = this.authorsCreateStore.validateData(
      this.authorsCreateStore.entityValues
    );

    // Uniqueness validation for subentity
    const currentValues = this.authorsCreateStore.entityValues;
    const existingRows = this.entityValues.showAuthors || [];
    const activeRows = existingRows.filter(
      (row: any) => !row.__status__?.includes("deleted")
    );

    // Uniqueness check: author
    const isDuplicate_author = activeRows.some(
      (row) =>
        row.id !== currentValues.id &&
        row.showAuthor?.id === currentValues.showAuthor?.id &&
        currentValues.showAuthor?.id
    );
    if (isDuplicate_author) {
      errors.author = {
        message: this.langStore.duplicateValueError,
        fieldLabel: this.langStore.WorkAuthorAuthorLabel,
      };
    }

    this.authorsCreateStore.errors = errors;
    return Object.keys(errors).length === 0;
  }

  authorsCommitChanges(changes: GridChangeSet) {
    if (changes.changed) {
      const values = this.authorsCreateStore.entityValues;
      const authors = this.entityValues.showAuthors;
      for (
        let index = 0;
        index < this.entityValues.showAuthors.length;
        index++
      ) {
        if (authors[index].id === values.id) {
          authors[index] = {
            ...values,
            __status__: updatedStatus(values.__status__),
          };
          break;
        }
      }
      this.authorsCreateStore.clearFields();
    } else if (changes.added) {
      const values = this.authorsCreateStore.entityValues;
      if (!this.entityValues.showAuthors) {
        this.entityValues.showAuthors = [];
      }
      this.entityValues.showAuthors = [
        ...this.entityValues.showAuthors,
        {
          ...values,
          id: `${this.uniqueCounter++}`,
          __status__: "added",
        },
      ];
      this.authorsCreateStore.clearFields();
    } else if (changes.deleted) {
      const id = changes.deleted[0];
      const authors = this.entityValues.showAuthors;
      for (
        let index = 0;
        index < this.entityValues.showAuthors.length;
        index++
      ) {
        if (authors[index].id === id) {
          if (isOriginalCrudStatus(authors[index].__status__)) {
            authors[index] = {
              ...authors[index],
              __status__: deleteStatus(authors[index].__status__),
            };
          } else {
            this.entityValues.showAuthors =
              this.entityValues.showAuthors.filter((x) => x.id !== id);
          }
          break;
        }
      }
    }

    // if (changes.changed) {
    // } else {
    //   const newChanges = getFixedChangeSet(changes, this.entityValues.authors);
    //   this.setCRUDRowsFromValues(newChanges, this.authorsCreateStore.entityValues);
    //   this.dummyCRUDRestoredObservable = this.uniqueCounter++;
    //   this.entityValues.authors = getNewCRUDRows(newChanges, this.entityValues.authors, this.uniqueCounter++);
    // }
  }

  getNotesValue(): string {
    return this.entityValues.notes;
  }

  setNotesValue(value: string) {
    this.entityValues.notes = value;
  }

  setPerformersOptions(values: InterpreterValuesType[]) {
    this.performersOptions = values;
  }

  *loadPerformersValues(): any {
    let where: any = null;
    if (this.performersSearchValue) {
      const searchFilter = {
        name: { contains: this.performersSearchValue, mode: "insensitive" },
      };
      where = where ? { AND: [where, searchFilter] } : searchFilter;
    }
    const api = yield* getApi(
      this.rootStore.authStore,
      this.rootStore.activityStore
    );
    const result = yield api.interpreterListWithOffset({
      offset: 0,
      take: 100,
      where: where,
      orderBy: [{ lastUpdatedAt_: SortOrder.Desc }],
    });
    this.setPerformersOptions(
      (result.interpreterListWithOffset || []).map((x: any) =>
        getInterpreterValues(x, this.langStore)
      )
    );
  }

  getPerformersOptions() {
    return this.performersOptions.map((x: any) => {
      return {
        displayId: resolveCurrentLang(x.displayId, this.langStore),
        id: x.id,
      };
    });
  }

  *setPerformersValue(value: string) {
    this.performersSearchValue = value;
    yield this.loadPerformersValues();
  }

  setSelectedPerformers(values: { displayId: string; id: string }[]) {
    if (values.length > 0 && values[values.length - 1].id === "add-new") {
      const notFound = /".*?"/.exec(values[values.length - 1].displayId);
      this.addNewInterpreter(
        notFound ? notFound[0].replaceAll('"', "") : undefined,
        "performers"
      );
    } else {
      this.entityValues.showPerformers = values;
      this.entityValues.__performers = values.map((v) => v.displayId);
    }
  }

  getCmsIdValue(): string {
    return this.entityValues.cmsId;
  }

  setCmsIdValue(value: string) {
    this.entityValues.cmsId = value;
  }

  setTerritoriesOptions(values: TerritoryValuesType[]) {
    this.territoriesOptions = values;
  }

  *loadTerritoriesValues(): any {
    const cache = this.rootStore.referenceCacheStore;
    yield cache.ensureLoaded("Territory");
    const allOptions = cache.getOptions("Territory") as TerritoryValuesType[];

    const searchValue = this.territoriesSearchValue?.toLowerCase() || "";
    let filtered = searchValue
      ? allOptions.filter((item: any) =>
          item.displayId.toLowerCase().includes(searchValue)
        )
      : [...allOptions];

    filtered.sort((a: any, b: any) => a.displayId.localeCompare(b.displayId));
    this.setTerritoriesOptions(filtered);
  }

  getTerritoriesOptions() {
    return this.territoriesOptions.map((x: any) => {
      return {
        displayId: resolveCurrentLang(x.displayId, this.langStore),
        id: x.id,
      };
    });
  }

  *setTerritoriesValue(value: string) {
    this.territoriesSearchValue = value;
    yield this.loadTerritoriesValues();
  }

  setSelectedTerritories(values: { displayId: string; id: string }[]) {
    if (values.length > 0 && values[values.length - 1].id === "add-new") {
      const notFound = /".*?"/.exec(values[values.length - 1].displayId);
      this.addNewTerritory(
        notFound ? notFound[0].replaceAll('"', "") : undefined,
        "territories"
      );
    } else {
      // Usar todas las opciones del cache para la lógica de exclusividad
      const cache = this.rootStore.referenceCacheStore;
      const allOptions = cache.getOptions("Territory") as TerritoryValuesType[];
      const processedValues = processTerritoriesSelection(
        values,
        this.entityValues.showTerritories,
        allOptions
      );
      this.entityValues.showTerritories = processedValues;
      this.entityValues.__territories = processedValues.map((v) => v.displayId);
    }
  }

  getIswcValue(): string {
    return this.entityValues.iswc;
  }

  setIswcValue(value: string) {
    this.entityValues.iswc = value;
  }

  scoreRestoreFile(id: string) {
    if (this.entityValues.score.length > 1) {
      alert("No puede haber más de un archivo activo " + id);
      return;
    }
  }

  *scoreUploadFile(file: File): any {
    const previousFiles = [...this.entityValues.__score_files];
    const previousName = this.entityValues.score;
    if (!file.name.toLowerCase().endsWith(".pdf")) {
      this.scoreError = this.langStore.MusicalWorkScoreLabel + ": solo se permiten archivos PDF";
      return;
    }
    this.scoreError = undefined;
    const fileId = `${this.uniqueCounter++}`;
    const newFile = {
      __status__: "added" as const,
      id: fileId,
      name: file.name,
      size: file.size,
      formattedSize: formatBytes(file.size),
      mimetype: file.type,
      ticket: "",
    };
    this.entityValues.__score_files.push(newFile);
    this.entityValues.score = file.name;
    this.dummyCRUDRestoredObservable = this.uniqueCounter++;

    const outcome = yield this.uploadTicket(file);
    if (!outcome.ticket) {
      // La subida no terminó: el servidor no tiene el archivo, así que no
      // puede quedar en la lista. Vuelve lo que había y el campo dice por qué.
      this.entityValues.__score_files = previousFiles;
      this.entityValues.score = previousName;
      this.dummyCRUDRestoredObservable = this.uniqueCounter++;
      if (outcome.cancelled) {
        // Lo canceló el usuario desde el modal de espera: no es una falla.
        this.clearFieldError("score");
      } else {
        this.setFieldError(
          "score",
          this.langStore.MusicalWorkScoreLabel,
          this.langStore.uploadFailedMessage(file.name, outcome.reason)
        );
      }
      return;
    }
    this.clearFieldError("score");
    const fileIndex = this.entityValues.__score_files.findIndex(
      (f) => f.id === fileId
    );
    if (fileIndex !== -1) {
      this.entityValues.__score_files[fileIndex].ticket = outcome.ticket;
    }
  }

  scoreCommitFile(changes: GridChangeSet) {
    if (changes.deleted) {
      const id = changes.deleted[0];
      let index = 0;
      let toRemove;
      for (let file of this.entityValues.__score_files) {
        if (file.id === id) {
          toRemove = index;
          break;
        }
        index++;
      }
      if (toRemove) {
        this.entityValues.__score_files.splice(toRemove, 1);
      }
    }
    this.dummyCRUDRestoredObservable = this.uniqueCounter++;
  }

  phonogramsLoadRow(row: any) {
    this.phonogramsCreateStore.loadValuesFromRow(row);
  }

  phonogramsRestore(id: string) {
    const phonograms = this.entityValues.showPhonograms.filter(
      (x) => x.id === id
    )[0];
    if (phonograms) {
      phonograms.__status__ = restoredStatus(phonograms.__status__);
    }
    this.dummyCRUDRestoredObservable = this.uniqueCounter++;
  }

  phonogramsValidateChanges(_changes: GridChangeSet) {
    const errors = this.phonogramsCreateStore.validateData(
      this.phonogramsCreateStore.entityValues
    );

    this.phonogramsCreateStore.errors = errors;
    return Object.keys(errors).length === 0;
  }

  phonogramsCommitChanges(changes: GridChangeSet) {
    if (changes.changed) {
      const values = this.phonogramsCreateStore.entityValues;
      const phonograms = this.entityValues.showPhonograms;
      for (
        let index = 0;
        index < this.entityValues.showPhonograms.length;
        index++
      ) {
        if (phonograms[index].id === values.id) {
          phonograms[index] = {
            ...values,
            __status__: updatedStatus(values.__status__),
          };
          break;
        }
      }
      this.phonogramsCreateStore.clearFields();
    } else if (changes.added) {
      const values = this.phonogramsCreateStore.entityValues;
      if (!this.entityValues.showPhonograms) {
        this.entityValues.showPhonograms = [];
      }
      this.entityValues.showPhonograms = [
        ...this.entityValues.showPhonograms,
        {
          ...values,
          id: `${this.uniqueCounter++}`,
          __status__: "added",
        },
      ];
      this.phonogramsCreateStore.clearFields();
    } else if (changes.deleted) {
      const id = changes.deleted[0];
      const phonograms = this.entityValues.showPhonograms;
      for (
        let index = 0;
        index < this.entityValues.showPhonograms.length;
        index++
      ) {
        if (phonograms[index].id === id) {
          if (isOriginalCrudStatus(phonograms[index].__status__)) {
            phonograms[index] = {
              ...phonograms[index],
              __status__: deleteStatus(phonograms[index].__status__),
            };
          } else {
            this.entityValues.showPhonograms =
              this.entityValues.showPhonograms.filter((x) => x.id !== id);
          }
          break;
        }
      }
    }

    // if (changes.changed) {
    // } else {
    //   const newChanges = getFixedChangeSet(changes, this.entityValues.phonograms);
    //   this.setCRUDRowsFromValues(newChanges, this.phonogramsCreateStore.entityValues);
    //   this.dummyCRUDRestoredObservable = this.uniqueCounter++;
    //   this.entityValues.phonograms = getNewCRUDRows(newChanges, this.entityValues.phonograms, this.uniqueCounter++);
    // }
  }

  getOriginalLyricsValue(): string {
    return this.entityValues.originalLyrics;
  }

  setOriginalLyricsValue(value: string) {
    this.entityValues.originalLyrics = value;
  }

  getContractObservationValue(): string {
    return this.entityValues.contractObservation;
  }

  setContractObservationValue(value: string) {
    this.entityValues.contractObservation = value;
  }

  getCreatedAt_Value(): Date | null {
    return this.entityValues.createdAt_;
  }

  setCreatedAt_Value(value: Date | null) {
    if (value !== null) {
      this.entityValues.createdAt_ = value;
    }
  }

  getLastUpdatedAt_Value(): Date | null {
    return this.entityValues.lastUpdatedAt_;
  }

  setLastUpdatedAt_Value(value: Date | null) {
    if (value !== null) {
      this.entityValues.lastUpdatedAt_ = value;
    }
  }

  *fileUpload(file: File): any {
    const data = yield apiUploadFile(true, file, this.rootStore.activityStore);
    // Una subida cancelada vuelve sin datos.
    return data?.singleUpload?.ticket;
  }

  *uploadTicket(file: File): any {
    // No tira: una subida que falla es un resultado que el campo tiene que
    // mostrar, no una excepción que se pierde en la consola.
    try {
      const ticket = yield this.fileUpload(file);
      // Sin ticket y sin error es un pedido abortado: el botón Cancelar.
      return ticket ? { ticket } : { cancelled: true };
    } catch (error) {
      return {
        reason: uploadFailureReason(error, this.langStore.uploadConnectionLost),
      };
    }
  }

  setFieldError(field: string, fieldLabel: string, message: string) {
    this.errors = {
      ...this.errors,
      [field]: { message, fieldLabel },
    } as MusicalWorkErrorsType;
  }

  clearFieldError(field: string) {
    if (!(field in this.errors)) return;
    const errors: Record<string, unknown> = { ...this.errors };
    delete errors[field];
    this.errors = errors as MusicalWorkErrorsType;
  }

  *fileDelete(ticket: string): any {
    const api = yield* getApi(
      this.rootStore.authStore,
      this.rootStore.activityStore
    );
    yield api.deleteUploadedFile({ ticket });
  }

  *addNewMusicalGenre(
    notFound: string | undefined,
    callerFieldName?: string
  ): any {
    // QuickCreate checks for fields with quickCreate enabled
    if (notFound && callerFieldName === "musicalGenre") {
      const confirmed = yield confirm(
        this.langStore.quickCreateConfirmTitle(
          this.langStore.MusicalGenreSingular
        ),
        this.langStore.quickCreateConfirmDescription(
          notFound,
          this.langStore.MusicalGenreSingular
        )
      );
      if (!confirmed) return;

      try {
        const api = yield getApiAsync(
          this.rootStore.authStore,
          this.rootStore.activityStore
        );
        const result = yield api.musicalGenreCreate({ name: notFound });

        if (result.musicalGenreCreate) {
          this.entityValues.showMusicalGenre = {
            id: result.musicalGenreCreate.id,
            displayId: result.musicalGenreCreate.displayId || notFound,
          };
          this.entityValues.__musicalGenre =
            result.musicalGenreCreate.displayId || notFound;
        }
      } catch (error) {
        if (error instanceof Error) {
          const apiError = getApiError(error, this.langStore);
          this.showErrorDialog(apiError.code, apiError.title, apiError.message);
        } else {
          this.showErrorDialog(this.langStore.serverError, undefined);
        }
      }
      return;
    }

    // Fallback to navigation for other fields
    this.isWaitingForCallback = true;
    this.callbackReturnPath = window.location.pathname;
    // Protect parent store in registry from destruction during navigation
    const parentStore = this.rootStore.storeRegistry.getStoreIfExists(
      this.callbackReturnPath
    );
    if (parentStore) {
      parentStore.isWaitingForCallback = true;
    }
    const callbackParams: CallbackDataParams = {
      storeId: this.callbackReturnPath,
      callerData: {
        entityName: "MusicalWork",
        fieldName: callerFieldName || "",
        isMulti: true,
        notFound: notFound,
      },
    };

    this.rootStore.router.goTo(
      "/musicalGenre/create",
      undefined,
      undefined,
      { callbackParams, initialDisplayId: notFound },
      this.breadcrumbLabel
    );
  }

  addNewInterpreter(notFound: string | undefined, callerFieldName?: string) {
    this.isWaitingForCallback = true;
    this.callbackReturnPath = window.location.pathname;
    // Protect parent store in registry from destruction during navigation
    const parentStore = this.rootStore.storeRegistry.getStoreIfExists(
      this.callbackReturnPath
    );
    if (parentStore) {
      parentStore.isWaitingForCallback = true;
    }
    const callbackParams: CallbackDataParams = {
      storeId: this.callbackReturnPath,
      callerData: {
        entityName: "MusicalWork",
        fieldName: callerFieldName || "",
        isMulti: true,
        notFound: notFound,
      },
    };

    this.rootStore.router.goTo(
      "/interpreter/create",
      undefined,
      undefined,
      { callbackParams, initialDisplayId: notFound },
      this.breadcrumbLabel
    );
  }

  addNewTerritory(notFound: string | undefined, callerFieldName?: string) {
    this.isWaitingForCallback = true;
    this.callbackReturnPath = window.location.pathname;
    // Protect parent store in registry from destruction during navigation
    const parentStore = this.rootStore.storeRegistry.getStoreIfExists(
      this.callbackReturnPath
    );
    if (parentStore) {
      parentStore.isWaitingForCallback = true;
    }
    const callbackParams: CallbackDataParams = {
      storeId: this.callbackReturnPath,
      callerData: {
        entityName: "MusicalWork",
        fieldName: callerFieldName || "",
        isMulti: true,
        notFound: notFound,
      },
    };

    this.rootStore.router.goTo(
      "/territory/create",
      undefined,
      undefined,
      { callbackParams, initialDisplayId: notFound },
      this.breadcrumbLabel
    );
  }

  clearFields() {
    this.savedId = null;
    this.cancelled = false;
    this.entityValues = MusicalWorkValuesInit();
    this.cardState = {
      null: { open: true, errors: [] },
      workInfo: { open: true, errors: [] },
      workRegistration: { open: true, errors: [] },
      workContent: { open: true, errors: [] },
      contracts: { open: true, errors: [] },
      systemDates: { open: false, errors: [] },
    };
    this.musicalGenreSearchValue = "";
    this.musicalGenreOptions = [];
    this.performersSearchValue = "";
    this.performersOptions = [];
    this.territoriesSearchValue = "";
    this.territoriesOptions = [];
  }

  clearErrors() {
    Object.keys(this.cardState).forEach((key) => {
      this.cardState[key].errors = [];
    });
  }

  validateData(values: MusicalWorkValuesType): MusicalWorkErrorsType {
    const errors: MusicalWorkErrorsType = {};
    this.clearErrors();
    values.title = values.title.trim();
    if (values.title === "") {
      errors.title = {
        message: this.langStore.mandatoryField,
        fieldLabel: this.langStore.MusicalWorkTitleLabel,
      };
      this.setCardError("workInfo", errors.title.message);
    }
    this.musicalGenreError = undefined;
    if (values.showMusicalGenre === null || values.showMusicalGenre.id === "") {
      errors.musicalGenre = {
        message: this.langStore.mandatoryField,
        fieldLabel: this.langStore.MusicalWorkMusicalGenreLabel,
      };
      this.setCardError("workInfo", errors.musicalGenre.message);
    }
    if (values.showAuthors.length === 0) {
      errors.authors = {
        message: this.langStore.atLeastOneValue,
        fieldLabel: this.langStore.MusicalWorkAuthorsLabel,
      };
      this.setCardError("workInfo", errors.authors.message);
    }
    if (values.showTerritories.length === 0) {
      errors.territories = {
        message: this.langStore.atLeastOneValue,
        fieldLabel: this.langStore.MusicalWorkTerritoriesLabel,
      };
      this.setCardError("workRegistration", errors.territories.message);
    }
    {
      const pendingUpload = values.__score_files.find(
        (f: any) => addedStatusList.includes(f.__status__) && !f.ticket
      );
      if (pendingUpload) {
        errors.score = {
          message: this.langStore.uploadNotFinishedMessage(pendingUpload.name),
          fieldLabel: this.langStore.MusicalWorkScoreLabel,
        };
        this.setCardError("workContent", errors.score.message);
      }
    }

    // Custom validators (registrados desde customValidations.ts). Se corren
    // siempre, aunque la entidad no tenga validaciones de campo generadas.
    const customValidationResult = runCustomValidators(
      this.customValidators,
      values,
      this.langStore,
      this.rootStore.referenceCacheStore
    );
    Object.assign(errors, customValidationResult.errors);
    if (customValidationResult.cardErrors) {
      for (const [cardId, messages] of Object.entries(
        customValidationResult.cardErrors
      )) {
        messages.forEach((msg) => this.setCardError(cardId, msg));
      }
    }

    this.setNavigationLinks();
    return errors;
  }

  // Avisos de los validators custom: ver collectCustomWarnings en validation/types.ts.
  get warnings(): Record<string, { message: string; fieldLabel: string }> {
    return collectCustomWarnings(
      this.customValidators,
      { ...this.entityValues },
      this.langStore,
      this.rootStore.referenceCacheStore,
      undefined,
      "MusicalWorkCreateStore"
    );
  }

  *duplicatesMusicalWork(id: string): any {
    const validToken = yield tokenManager.getValidAccessToken();
    if (!validToken) {
      throw new Error("SESSION_EXPIRED");
    }
    this.rootStore.authStore.accessToken = validToken;
    const musicalWorkInstance = yield this.service.getOne(
      id,
      validToken,
      this.rootStore.activityStore
    );
    if (musicalWorkInstance) {
      this.entityValues = getMusicalWorkValues(
        musicalWorkInstance,
        this.langStore,
        true
      );
    }
  }

  close(returnData?: { resultId?: string; cancelled?: boolean; entity?: any }) {
    if (this.closeHandler) {
      this.closeHandler(returnData);
      return;
    }

    if (this.isModalMode) {
      // In modal mode, close or pop the modal instead of navigating
      if (this.rootStore.modalViewStack.length > 1) {
        this.rootStore.popModalView();
      } else {
        this.rootStore.closeModalView();
      }
      return;
    }

    if (this.callbackReturnPath) {
      // Sync breadcrumbs to return path (truncates stack instead of clearing)
      this.rootStore.syncBreadcrumbsWithBrowser(this.callbackReturnPath);

      // Navigate back with callback data (include original callerData)
      // Use JSON parse/stringify to ensure the state is structurally clonable for History API
      const callbackParams: CallbackDataParams = JSON.parse(
        JSON.stringify({
          storeId: this.callbackReturnPath,
          callerData: this.callbackParams?.callerData,
          returnData: returnData || { cancelled: true },
        })
      );
      this.rootStore.router.replace(this.callbackReturnPath, {
        callbackParams,
      });
    } else if (returnData?.resultId && !this.rootStore.router.canGoBack()) {
      // Sin página anterior (una pestaña nueva), volver atrás no navega: el
      // formulario quedaría abierto, y un segundo Guardar daría de alta otro
      // registro. Pasa a la edición del registro creado.
      this.rootStore.router.replace(
        `/musicalWork/update/${returnData.resultId}`
      );
    } else {
      this.rootStore.router.goBack();
    }
  }

  *saveNewMusicalWork(): any {
    // Un segundo Guardar mientras corre el primero no hace nada: daría de alta
    // el registro dos veces.
    if (this.isProcessing) return;
    this.errors = { ...this.validateData(this.entityValues) };
    if (Object.keys(this.errors).length === 0) {
      this.closeValidationErrorsDialog();

      // El id de ESTE guardado. Si falla antes de llegar al server (al armar el
      // pedido), no queda el de uno anterior que cierre como si éste hubiera
      // salido bien.
      this.savedId = null;
      // Mientras manda, el formulario está bloqueado por su capa.
      this.isSaving = true;
      try {
        this.status = "processing";
        yield this._save();
        this.status = "ok";
      } catch (error) {
        this.status = "error";

        if (error instanceof Error) {
          const apiError = getApiError(error, this.langStore);
          this.showErrorDialog(apiError.code, apiError.title, apiError.message);
        } else {
          this.showErrorDialog(this.langStore.serverError, undefined);
        }
      } finally {
        this.isSaving = false;
      }

      if (this.savedId) {
        this.saveOriginalState();
        this.close({
          resultId: this.savedId,
          entity: { id: this.savedId, displayId: this.displayId },
          cancelled: false,
        });
      }
    } else {
      this.showValidationErrorsDialog();
    }
  }

  cancelNewMusicalWork(): void {
    this.cancelled = true;

    // Navigation handled by component

    this.close({ cancelled: true });
  }

  get pendingChanges(): boolean {
    return this._ownPendingChanges;
  }

  hasUnsavedChanges(): boolean {
    return this.pendingChanges;
  }

  // Public interface for navigation blocking system
  get hasPendingChanges(): boolean {
    return this.pendingChanges;
  }

  /**
   * Computes a normalized snapshot for change detection.
   * - Simple fields: direct value
   * - Single refs: normalized to ID only
   * - Multi refs: normalized to sorted array of IDs
   */
  private computeSnapshot(): Record<string, any> {
    return {
      title: this.entityValues.title,
      notes: this.entityValues.notes,
      cmsId: this.entityValues.cmsId,
      iswc: this.entityValues.iswc,
      originalLyrics: this.entityValues.originalLyrics,
      contractObservation: this.entityValues.contractObservation,
      createdAt_: this.entityValues.createdAt_,
      lastUpdatedAt_: this.entityValues.lastUpdatedAt_,
      musicalGenre: this.entityValues.showMusicalGenre?.id || null,
      performers: (this.entityValues.showPerformers || [])
        .map((x: any) => x.id)
        .sort(),
      territories: (this.entityValues.showTerritories || [])
        .map((x: any) => x.id)
        .sort(),
    };
  }

  private setupChangeDetection() {
    // Use normalized snapshot with deep equality comparison
    this.stopReaction = reaction(
      () => this.computeSnapshot(),
      (snapshot) => {
        const hasChanges = this.originalSnapshot
          ? !_.isEqual(snapshot, this.originalSnapshot)
          : false;

        if (this._ownPendingChanges !== hasChanges) {
          this._ownPendingChanges = hasChanges;
        }
      },
      { fireImmediately: false, equals: (a, b) => _.isEqual(a, b) }
    );
  }

  private saveOriginalState() {
    this.originalSnapshot = this.computeSnapshot();
    this._ownPendingChanges = false;
  }

  getState(): InitializeParams {
    return {
      uiState: {
        cardState: this.cardState,
      },
    };
  }

  loadValuesFromRow(row: any) {
    const values = getMusicalWorkValues(row, this.langStore);
    // Filas nuevas de la grilla traen SOLO las columnas visibles: los campos
    // ausentes (undefined) caen al init — sin esto, validaciones tipo
    // values.x.trim() explotan y los inputs quedan uncontrolled.
    const init = MusicalWorkValuesInit();
    for (const k of Object.keys(init) as (keyof typeof init)[]) {
      if ((values as any)[k] === undefined)
        (values as any)[k] = (init as any)[k];
    }
    this.entityValues = values;
  }

  *_save(): any {
    const cache = this.rootStore.referenceCacheStore;
    const allTerritoryOptions = cache.getOptions("Territory") as TerritoryValuesType[];

    // Validar exclusividad de "Todo el mundo"
    const territoryError = validateTerritoriesExclusivity(
      this.entityValues.showTerritories,
      allTerritoryOptions
    );
    if (territoryError) {
      throw new Error(territoryError);
    }

    // Optimizar territorios: si todos los países están seleccionados → solo "Todo el mundo"
    const optimizedTerritories = optimizeTerritoriesForSave(
      this.entityValues.showTerritories,
      allTerritoryOptions
    );
    const entityValuesForSave = {
      ...this.entityValues,
      showTerritories: optimizedTerritories,
    };

    const variables = getMusicalWorkCreateVariables(entityValuesForSave);
    this.savedId = null;
    this.savedEntity = null;
    const api = yield* getApi(
      this.rootStore.authStore,
      this.rootStore.activityStore
    );
    const result = yield api.musicalWorkCreate({
      ...variables,
      idempotencyKey_: this.idempotencyKeyFor(variables),
    });
    this.savedId = result.musicalWorkCreate.id;
    this.saveAttempt = null;
    this.savedEntity = {
      ...this.entityValues,
      id: result.musicalWorkCreate.id,
    } as any;
  }

  *process(fn: AsyncFunction) {
    try {
      this.status = "processing";
      yield fn();
      this.status = "ok";
    } catch (error) {
      // El estado primero: si el diálogo fallara, Guardar no queda trabado.
      this.status = "error";
      if (error instanceof Error) {
        const apiError = getApiError(error, this.langStore);
        this.showErrorDialog(apiError.code, apiError.title, apiError.message);
      } else {
        this.showErrorDialog(this.langStore.serverError, undefined);
      }
    }
  }

  *initialize(params?: InitializeParams) {
    try {
      this.status = "processing";

      // Register custom validators

      const duplicateFromId = params?.duplicateFromId;

      // Si se proporciona un ID para duplicar (desde router.params)
      if (duplicateFromId) {
        yield this.duplicatesMusicalWork(duplicateFromId);
      }

      // Si se proporciona initialDisplayId (desde "Añadir 'texto'" en autocomplete)
      const initialDisplayId = params?.initialDisplayId;
      if (initialDisplayId) {
        this.entityValues.title = initialDisplayId;
      }

      yield this.loadMusicalGenreValues();
      yield this.loadPerformersValues();
      yield this.loadTerritoriesValues();

      this.status = "ok";
    } catch (error) {
      this.status = "error";
      if (error instanceof Error) {
        const apiError = getApiError(error, this.langStore);
        let message = apiError.message;
        this.showErrorDialog(error, apiError.title, message);
      } else {
        this.showErrorDialog(error, this.langStore.serverError, undefined);
      }
    }
  }
}
